import { useEffect, useRef, useState, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { showEmailNotification } from '@/components/ui/EmailNotification';
import { useFolderStore } from '@/store/folderStore';
import api from '@/services/api';
import { authService } from '@/services/auth';
import { requestNotificationPermission, onMessageListener } from '@/config/firebase';
import { EMAIL_UPDATE_EVENT } from './useFirebaseNotification';

interface NotificationData {
  type: string;
  folder: string;
  email_id: string;
  totalCount: string;
  newCount: string;
  unseenCount: string;
  title: string;
  body: string;
  email_uid?: string;
}

export const useUnifiedNotifications = (socketRef: React.RefObject<Socket>) => {
  const [isFirebaseAvailable, setIsFirebaseAvailable] = useState(false);
  const isProcessingRef = useRef(false);
  const notificationQueueRef = useRef<NotificationData[]>([]);
  const { updateFolderCounts } = useFolderStore();
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio on mount
  useEffect(() => {
    notificationSoundRef.current = new Audio('/notification.mp3');
    notificationSoundRef.current.load();

    return () => {
      if (notificationSoundRef.current) {
        notificationSoundRef.current.pause();
        notificationSoundRef.current = null;
      }
    };
  }, []);

  // Process notification queue with retries
  const processNotificationQueue = useCallback(async () => {
    if (isProcessingRef.current || notificationQueueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    try {
      const notification = notificationQueueRef.current[0];
      
      // Update folder counts
      if (notification.folder) {
        try {
          await updateFolderCounts(
            notification.folder,
            parseInt(notification.totalCount || '0'),
            parseInt(notification.totalCount),
            parseInt(notification.totalCount || '0')
          );
        } catch (err) {
          console.error('Failed to update folder counts:', err);
        }
      }
      
      // Fetch new email details and update local storage
      if (notification.email_id) {
        try {
          const response = await api.get(`/api/email/get_by_id/${notification.email_id}`);
          const newEmail = response.data;

          // Get current folder's emails from localStorage
          const localKey = `emails_${notification.folder}_1`;
          const cached = localStorage.getItem(localKey);

          if (cached) {
            try {
              const parsedCache = JSON.parse(cached);
              
              // Add new email to the beginning of the list
              const updatedEmails = {
                ...parsedCache,
                emails: [newEmail, ...(parsedCache.emails || [])]
              };

              // Update localStorage
              localStorage.setItem(localKey, JSON.stringify(updatedEmails));
              
              // Create event detail with all required data
              const eventDetail = {
                type: notification.type,
                folder: notification.folder,
                email_data: newEmail,
                email_id: notification.email_id,
                email_uid: notification.email_uid,
                newCount: notification.newCount,
                click_action: 'OPEN_INBOX',
                totalCount: notification.totalCount,
                timestamp: new Date().toISOString()
              };
              
              window.dispatchEvent(new CustomEvent(EMAIL_UPDATE_EVENT, { detail: eventDetail }));
            } catch (err) {
              console.error('Failed to update localStorage:', err);
              throw err; // Propagate error for retry
            }
          }
        } catch (err) {
          console.error('Failed to fetch and update new email:', err);
          if (retryCount < MAX_RETRIES) {
            retryCount++;
            setTimeout(processNotificationQueue, 1000);
            return;
          }
          throw err;
        }
      }

      // Play sound
      if (notificationSoundRef.current) {
        notificationSoundRef.current.currentTime = 0;
        try {
          await notificationSoundRef.current.play();
        } catch (err) {
          console.warn('Could not play notification sound:', err);
        }
      }

      // Show toast notification
      try {
        showEmailNotification({
          title: notification.title,
          body: notification.body,
          folder: notification.folder,
          email_id: notification.email_id,
          email_uid: notification.email_uid || '',
          newCount: notification.newCount,
          totalCount: notification.totalCount
        });

        // Wait for toast to complete
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 2000);
        });

        // Remove processed notification only after successful processing
        notificationQueueRef.current.shift();
        
        // Process next notification if any
        if (notificationQueueRef.current.length > 0) {
          setTimeout(processNotificationQueue, 500);
        }
      } catch (error) {
        console.error('Failed to show notification:', error);
        retryCount++;
        
        if (retryCount < MAX_RETRIES) {
          setTimeout(processNotificationQueue, 1000);
          return;
        } else {
          console.error(`Failed to process notification after ${MAX_RETRIES} retries:`, notification);
          notificationQueueRef.current.shift();
        }
      }
    } catch (error) {
      console.error('Error processing notification:', error);
      retryCount++;
      
      if (retryCount < MAX_RETRIES) {
        setTimeout(processNotificationQueue, 1000);
        return;
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [updateFolderCounts]);

  // Initialize Firebase
  useEffect(() => {
    const initializeFirebase = async () => {
      try {
        const user = authService.getCurrentUser();
        if (!user?.username) return;

        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
          setIsFirebaseAvailable(false);
          return;
        }

        // Check if push service is available
        try {
          const registration = await navigator.serviceWorker.ready;
          const pushManager = await registration.pushManager.getSubscription();
          if (!pushManager && !('safari' in window)) {
            setIsFirebaseAvailable(false);
            return;
          }
        } catch (err) {
          setIsFirebaseAvailable(false);
          return;
        }

        const token = await requestNotificationPermission();
        if (!token) {
          setIsFirebaseAvailable(false);
          return;
        }

        await api.post('/api/notifications/register', { token });
        setIsFirebaseAvailable(true);
      } catch (error) {
        setIsFirebaseAvailable(false);
      }
    };

    initializeFirebase();
  }, []);

  // Handle Firebase notifications
  useEffect(() => {
    if (!isFirebaseAvailable) return;

    const unsubscribe = onMessageListener((payload: any) => {
      const notification: NotificationData = {
        type: payload.data?.type || 'new_messages',
        folder: payload.data?.folder || '',
        email_id: payload.data?.email_id || '',
        totalCount: payload.data?.totalCount || '0',
        newCount: payload.data?.newCount || '0',
        unseenCount: payload.data?.unseenCount || '0',
        title: payload.notification?.title || payload.data?.title || 'New Email',
        body: payload.notification?.body || payload.data?.body || 'You have received a new email',
        email_uid: payload.data?.email_uid
      };
      
      notificationQueueRef.current.push(notification);
      if (!isProcessingRef.current && notificationQueueRef.current.length === 1) {
        processNotificationQueue();
      }
    });

    return () => {
      unsubscribe();
      notificationQueueRef.current = [];
      isProcessingRef.current = false;
    };
  }, [isFirebaseAvailable, processNotificationQueue]);

  // Handle Socket notifications
  useEffect(() => {
    if (isFirebaseAvailable || !socketRef.current) return;

    socketRef.current.on('notification', (data: any) => {
      const notification: NotificationData = {
        type: data.data.type,
        folder: data.data.folder,
        email_id: data.data.email_id,
        totalCount: data.data.totalCount,
        newCount: data.data.newCount,
        unseenCount: data.data.unseenCount,
        title: data.notification?.title || data?.title || 'New Email',
        body: data.notification?.body || data?.body || 'You have received a new email',
        email_uid: data.data.email_uid
      };

      notificationQueueRef.current.push(notification);
      if (!isProcessingRef.current && notificationQueueRef.current.length === 1) {
        processNotificationQueue();
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off('notification');
      }
      notificationQueueRef.current = [];
      isProcessingRef.current = false;
    };
  }, [isFirebaseAvailable, socketRef.current, processNotificationQueue]);

  return {
    isFirebaseAvailable,
    isProcessing: isProcessingRef.current,
    queueLength: notificationQueueRef.current.length
  };
};